/* global Word */

import type { TemplateProfile } from "../../templates/types";
import { logger } from "../../core/logging";

// ─── Internal constants ───────────────────────────────────────────────────────

const EXHIBIT_TITLE_TEXT = " Title or description here";
const EXHIBIT_HEADER_REGEX = /^Exhibit\s+(\d+)\s*:/i;

// ─── Internal helpers ─────────────────────────────────────────────────────────

function buildTitleSuffix(paraText: string): string {
  const match = paraText.match(/^Exhibit\s+\d+\s*:\s*(.*)$/i);
  const suffix = match?.[1]?.trim();
  return suffix && suffix.length > 0 ? ` ${suffix}` : EXHIBIT_TITLE_TEXT;
}

function writeHeaderRuns(
  para: Word.Paragraph,
  exhibitNumber: number,
  titleSuffix: string,
  profile: TemplateProfile
): void {
  const labelRun = para.insertText(
    `Exhibit ${exhibitNumber}:`,
    Word.InsertLocation.start
  );
  labelRun.font.name  = profile.typography.fontFamily;
  labelRun.font.size  = profile.typography.headerFontSize;
  labelRun.font.bold  = true;
  labelRun.font.color = profile.colorPalette.exhibitLabel;

  const descRun = para.insertText(titleSuffix, Word.InsertLocation.end);
  descRun.font.name  = profile.typography.fontFamily;
  descRun.font.size  = profile.typography.headerFontSize;
  descRun.font.bold  = true;
  descRun.font.color = profile.colorPalette.exhibitDesc;
}

// ─── Public: header text writer ──────────────────────────────────────────────

/**
 * Writes exhibit label and description runs onto a paragraph.
 * Called by setupHeaderCell — the paragraph must already be loaded and cleared.
 */
export function setExhibitHeaderText(
  para: Word.Paragraph,
  exhibitNumber: number,
  profile: TemplateProfile
): void {
  writeHeaderRuns(para, exhibitNumber, EXHIBIT_TITLE_TEXT, profile);
}

// ─── Public: next number resolver ────────────────────────────────────────────

/**
 * Returns the next exhibit number by reading body.text with a regex.
 *
 * Intentionally simple — reads the entire body as a single string and
 * extracts the highest "Exhibit N:" label. No table scanning, no complex
 * load chains, no failure modes from collection.items access.
 */
export async function getNextExhibitNumber(
  context: Word.RequestContext
): Promise<number> {
  context.document.body.load("text");
  await context.sync();

  const bodyText = context.document.body.text ?? "";
  const pattern = /\bExhibit\s+(\d+)\s*:/gi;

  let maxNumber = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(bodyText)) !== null) {
    const parsed = parseInt(match[1], 10);
    if (!isNaN(parsed) && parsed > maxNumber) {
      maxNumber = parsed;
    }
  }

  return maxNumber + 1;
}

// ─── Public: renumberer ───────────────────────────────────────────────────────

/**
 * Scans all tables in the document body and renumbers exhibit headers
 * in document order.
 *
 * Detection strategy — dual-read for maximum Office Online compatibility:
 *   • Loads both cell.body.text AND the first paragraph's text
 *   • Uses either one (whichever is non-empty) to match the exhibit pattern
 *   • Input is trimmed before regex test to handle leading \\r\\n from Office Online
 *
 * Load sequence:
 *   1. Load all tables
 *   2. Load rows for every table (batched)
 *   3. Load cells of first row for every table (batched)
 *   4. Load cell.body.text + cell.body.paragraphs for col-0 and col-2 (batched)
 *   5. Load para.text for those paragraphs (batched)
 *   6. Detect headers, write renumbered runs
 *   7. Single final sync
 */
export async function renumberExhibitTables(
  context: Word.RequestContext,
  profile: TemplateProfile
): Promise<void> {
  // Step 1 — load all tables
  const tables = context.document.body.tables;
  tables.load("items");
  await context.sync();

  logger.debug("[renumber] Scanning tables", { count: tables.items.length });

  if (tables.items.length === 0) return;

  // Step 2 — load rows for every table in one batch
  for (const table of tables.items) {
    table.rows.load("items");
  }
  await context.sync();

  // Step 3 — load first-row cells for tables that have rows (batched)
  for (const table of tables.items) {
    if (table.rows.items.length === 0) continue;
    table.rows.items[0].cells.load("items");
  }
  await context.sync();

  // Step 4 — load BOTH cell.body.text AND paragraphs for col-0 and col-2.
  // Dual-read: Office Online may return "" for cell.body.text OR para.text
  // depending on how the text was written. Loading both and using whichever
  // is non-empty makes detection reliable across environments.
  for (const table of tables.items) {
    if (table.rows.items.length === 0) continue;
    const cells = table.rows.items[0].cells.items;
    if (cells.length === 0) continue;

    cells[0].body.load("text");
    cells[0].body.paragraphs.load("items");
    if (cells.length > 2) {
      cells[2].body.load("text");
      cells[2].body.paragraphs.load("items");
    }
  }
  await context.sync();

  // Step 5 — load para.text for first paragraph of each candidate cell (batched)
  for (const table of tables.items) {
    if (table.rows.items.length === 0) continue;
    const cells = table.rows.items[0].cells.items;
    if (cells.length === 0) continue;

    const paras0 = cells[0].body.paragraphs.items;
    if (paras0.length > 0) {
      paras0[0].load("text");
      paras0[0].load("leftIndent");
      paras0[0].load("rightIndent");
    }

    if (cells.length > 2) {
      const paras2 = cells[2].body.paragraphs.items;
      if (paras2.length > 0) {
        paras2[0].load("text");
        paras2[0].load("leftIndent");
        paras2[0].load("rightIndent");
      }
    }
  }
  await context.sync();

  // Step 6 — detect exhibit headers using both cell.body.text and para.text.
  // Trim before matching to handle Office Online's leading \r\n in body.text.
  // Loose regex (no ^ anchor) used for detection only.
  const LOOSE_DETECT = /Exhibit\s+\d+\s*:/i;

  const headerCells: Array<{ cell: Word.TableCell; text: string }> = [];

  for (let t = 0; t < tables.items.length; t++) {
    const table = tables.items[t];
    if (table.rows.items.length === 0) continue;
    const cells = table.rows.items[0].cells.items;
    if (cells.length === 0) continue;

    // Col 0
    const cellText0  = (cells[0].body.text ?? "").trim();
    const paras0     = cells[0].body.paragraphs.items;
    const paraText0  = paras0.length > 0 ? (paras0[0].text ?? "").trim() : "";
    const bestText0  = cellText0 || paraText0;

    logger.debug("[renumber] Table col0 read", { index: t, cellBodyText: cellText0, paraText: paraText0 });

    if (LOOSE_DETECT.test(bestText0)) {
      headerCells.push({ cell: cells[0], text: bestText0 });
    }

    // Col 2 (double layouts only)
    if (cells.length > 2) {
      const cellText2 = (cells[2].body.text ?? "").trim();
      const paras2    = cells[2].body.paragraphs.items;
      const paraText2 = paras2.length > 0 ? (paras2[0].text ?? "").trim() : "";
      const bestText2 = cellText2 || paraText2;

      logger.debug("[renumber] Table col2 read", { index: t, cellBodyText: cellText2, paraText: paraText2 });

      if (LOOSE_DETECT.test(bestText2)) {
        headerCells.push({ cell: cells[2], text: bestText2 });
      }
    }
  }

  logger.debug("[renumber] Exhibit headers found", { count: headerCells.length });

  if (headerCells.length === 0) return;

  // Step 7 — renumber in document order and write updated runs
  let counter = 1;

  for (const { cell, text } of headerCells) {
    const paras = cell.body.paragraphs.items;
    if (paras.length === 0) continue;

    const suffix = buildTitleSuffix(text);
    logger.debug("[renumber] Assigning exhibit number", { exhibitNumber: counter, suffix });

    // Preserve indents before clearing paragraph styles
    const originalLeftIndent = paras[0].leftIndent;
    const originalRightIndent = paras[0].rightIndent;

    if (paras.length > 1) {
      for (let i = 1; i < paras.length; i++) {
        paras[i].delete();
      }
    }

    paras[0].clear();
    // Re-apply paragraph formatting after clear
    paras[0].alignment = Word.Alignment.justified;
    paras[0].spaceBefore = 0;
    paras[0].spaceAfter = 0;
    paras[0].lineUnitBefore = 0;
    paras[0].lineUnitAfter = 0;
    paras[0].leftIndent = originalLeftIndent;
    paras[0].rightIndent = originalRightIndent;
    
    writeHeaderRuns(paras[0], counter, suffix, profile);
    counter++;
  }

  // Step 8 — single sync for all writes
  await context.sync();

  logger.info("[renumber] Renumbering complete", { count: counter - 1 });
}

